// Spark's fixed lists and labels. The API holds the same lists in
// api/src/spark/spark-rules.js; behaviour lives in web/docs/features/spark.md.

import { CATEGORY } from '../components/ui/categoryColors';
import type { CategoryName } from '../components/ui/categoryColors';

// Hex, not oklch(): Tag's tint() and readable() and the board's lane heads parse it.
const ink = (name: CategoryName) => CATEGORY[name].ink.hex;

export type Channel = 'shows' | 'systems' | 'marketing';
export type Speciality = 'shows' | 'systems' | 'marketing';
export type Stage = 'rtf' | 'fin' | 'cre' | 'stg' | 'pln' | 'upn' | 'ond' | 'inq' | 'wait';
export type Runway = 'long' | 'standard' | 'rush';
export type StudioRole = 'director' | 'designer' | 'animator' | 'animation_developer' | 'illustrator';
export type TeamRole = 'producer' | 'designer' | 'pilot';
export type TaskStatus = 'not_started' | 'in_progress' | 'finalizing' | 'revisions' | 'ready' | 'closed';
export type ReviewStep = 'designer' | 'director' | 'producer' | 'client' | 'pilot';
export type ReviewState = 'not_sent' | 'designer' | 'director' | 'producer' | 'client' | 'changes' | 'reviewed';

export const CHANNELS: { key: Channel; label: string; lede: string }[] = [
  { key: 'shows', label: 'Shows', lede: 'Every show in production, closest to flying first.' },
  { key: 'systems', label: 'Systems', lede: 'Systems work. People are picked by hand.' },
  { key: 'marketing', label: 'Marketing', lede: 'Marketing work. People are picked by hand.' },
];

/**
 * Michael's pipeline, in his column order, with his hues from the category family. Up Next sits on
 * sky and On Deck on fuchsia so the three Incoming stages stay apart at matched lightness.
 */
export const PIPELINE: { key: Stage; label: string; color: string }[] = [
  { key: 'rtf', label: 'RTF', color: ink('green') },
  { key: 'fin', label: 'Finalizing', color: ink('neutral') },
  { key: 'cre', label: 'In Progress 1 - Creating', color: ink('red') },
  { key: 'stg', label: 'In Progress 2 - Staged', color: ink('orange') },
  { key: 'pln', label: 'In Progress 3 - Planning', color: ink('amber') },
  { key: 'upn', label: 'Incoming 1 - Up Next', color: ink('sky') },
  { key: 'ond', label: 'Incoming 2 - On Deck', color: ink('fuchsia') },
  { key: 'inq', label: 'Incoming 3 - In Queue', color: ink('indigo') },
  { key: 'wait', label: 'Wait', color: ink('yellow') },
];

/** A project's Status: what it needs next, at any pipeline column. Review Status is its own field. */
export const STATUS_REASONS: { label: string; color: string }[] = [
  { label: 'Client List Needed', color: ink('neutral') },
  { label: 'Show Script Needed', color: ink('teal') },
  { label: 'Concept Slides Needed', color: ink('blue') },
  { label: 'Illustrated Storyboard Needed', color: ink('blue') },
  { label: 'Drone Plots Needed', color: ink('purple') },
  { label: 'Animated Bricks Needed', color: ink('fuchsia') },
  { label: 'Show Preview Needed', color: ink('pink') },
  { label: 'Studio Coloring', color: ink('yellow') },
  { label: 'Under Revisions', color: ink('amber') },
];
export const REVIEW_COLOR = ink('red');

/**
 * Review Status, in the filter's order. `label` is the field's value, `chip` the filter's word,
 * `rank` how far along it is: not sent lowest, changes asked just above it, reviewed highest.
 */
export const REVIEW_STATES: { key: ReviewState; label: string; chip: string; rank: number }[] = [
  { key: 'not_sent', label: 'Not sent', chip: 'Not sent', rank: 0 },
  { key: 'designer', label: 'Needs Designer Review', chip: 'Needs designer', rank: 2 },
  { key: 'director', label: 'Needs Director Review', chip: 'Needs director', rank: 3 },
  { key: 'producer', label: 'Needs Producer Review', chip: 'Needs producer', rank: 4 },
  { key: 'client', label: 'Needs Client Review', chip: 'Needs client', rank: 5 },
  { key: 'changes', label: 'Changes asked', chip: 'Changes asked', rank: 1 },
  { key: 'reviewed', label: 'Reviewed', chip: 'Reviewed', rank: 6 },
];

export const reviewStateOf = (key: ReviewState) => REVIEW_STATES.find((s) => s.key === key) || REVIEW_STATES[0];

export const SPECIALITIES: { key: Speciality; label: string; channel: Channel }[] = [
  { key: 'shows', label: 'Shows', channel: 'shows' },
  { key: 'systems', label: 'Systems', channel: 'systems' },
  { key: 'marketing', label: 'Marketing', channel: 'marketing' },
];

export const WORK_STEPS = ['Animation', 'Motion Graphics', 'Show Building', 'Development', 'Testing', 'Training', 'Customer Support'];

/** Short form is the label; the reference is the hover (fourth look). */
export const SPICE: { level: number; label: string; short: string; reference: string }[] = [
  { level: 1, label: 'Mild', short: 'Minimum effort', reference: 'At least one attention-grabbing feature and a soak every 5 seconds' },
  { level: 2, label: 'Medium', short: 'Standard effort', reference: 'At least one attention-grabbing feature and a soak every 3 seconds' },
  { level: 3, label: 'Hot', short: 'Extra effort', reference: 'At least one attention-grabbing feature and a soak every second' },
  { level: 4, label: 'Fire', short: 'Maximum effort', reference: 'Every second, and it is perfect' },
  { level: 5, label: 'Diablo', short: 'Maximum effort with no runway', reference: 'Fire, with very little to no runway' },
];

export const RUNWAYS: { key: Runway; label: string; days: number }[] = [
  { key: 'long', label: 'Long Form / Masterpiece', days: 42 },
  { key: 'standard', label: 'Standard', days: 14 },
  { key: 'rush', label: 'Pop Up / Rush', days: 5 },
];

export const STUDIO_ROLES: { key: StudioRole; label: string }[] = [
  { key: 'director', label: 'Director' },
  { key: 'designer', label: 'Designer' },
  { key: 'animator', label: 'Animator' },
  { key: 'animation_developer', label: 'Animation Developer' },
  { key: 'illustrator', label: 'Illustrator' },
];

export const TASK_STATUS: Record<TaskStatus, { label: string; color: string }> = {
  not_started: { label: 'Not started', color: ink('neutral') },
  in_progress: { label: 'In progress', color: ink('blue') },
  finalizing: { label: 'In review', color: ink('red') },
  revisions: { label: 'Revisions Needed', color: ink('amber') },
  ready: { label: 'Ready to Fly', color: ink('green') },
  closed: { label: 'Closed with the event', color: ink('neutral') },
};

export const firstName = (name: string | null | undefined) => (name ? name.split(' ')[0] : '');

/**
 * Who the assignee picker lists first for a deliverable (third look). It guides,
 * it never locks: "Show everyone" lists the rest.
 */
const TAKES: Record<string, StudioRole> = {
  Animating: 'animator',
  'Studio Building': 'animator',
  'Studio Transition Video File': 'animator',
  Animation: 'animator',
  'Show Building': 'animator',
  'Concept Sketches / Show Blocking': 'illustrator',
  'Animation Development': 'animation_developer',
  Development: 'animation_developer',
};
const DEV_ONLY = ['Animation Development', 'Development'];

export function eligibleFor(deliverable: string, roles: StudioRole[]): boolean {
  const need = TAKES[deliverable];
  if (need && roles.includes(need)) return true;
  if (DEV_ONLY.includes(deliverable)) return false;
  return roles.includes('director') || roles.includes('designer');
}

export function eligibilityText(deliverable: string): string {
  const need = TAKES[deliverable];
  if (!need) return 'Directors and Designers take this. The rest of the Studio is one click away.';
  if (need === 'animation_developer') return 'Animation Developers take this.';
  return `${need === 'animator' ? 'Animators' : 'Illustrators'} take this. Directors and Designers can too.`;
}

/**
 * The Studio's GitBook pages, held in one list in code (no search, no live sync).
 * Fill each url with the real page before launch.
 */
export const RESOURCES: { title: string; summary: string; url: string }[] = [
  { title: 'Spice levels, explained', summary: 'How Mild through Diablo are decided', url: '' },
  { title: 'Review checklist for designers', summary: 'What to look at before you pass a preview', url: '' },
  { title: 'Runway and lead times', summary: 'When a preview is due for each kind of show', url: '' },
  { title: 'Naming a formation', summary: 'How Stock formations are titled and filed', url: '' },
  { title: 'Pyro plans and the packing list', summary: 'What packing needs from the creative team', url: '' },
  { title: 'Handing a preview to a client', summary: 'What goes in the message and the folder', url: '' },
];
